// src/features/calls/callService.ts
// Canonical call-session persistence for AX1 ChatHub.

import { demoAvatarFor } from '@/demo/demoAvatars';
import { isDemoMode, supabase } from '../../lib/supabase';
import { PROVIDER_NAME, resolveGoogleMeetJoinUrl, type GoogleMeetConfig } from './providers/googleMeetProvider';

export type CallIntent = 'call' | 'video';
export type CallStatus = 'planned' | 'active' | 'ended' | 'cancelled';
export type CallCreatedVia = 'instant' | 'planned';
export type CallProvider = 'google_meet' | 'microsoft_teams' | 'fallback';
export type ProviderStatus = 'created' | 'synced' | 'error' | 'fallback';

/** Mirrors public.call_sessions exactly. */
export interface CallSession {
  id: string;
  workspace_id: string;
  project_id: string | null;
  room_id: string | null;
  calendar_event_id: string | null;
  title: string;
  provider: string | null;
  provider_room_id: string | null;
  status: CallStatus;
  starts_at: string | null;
  ended_at: string | null;
  created_by: string;
  created_at: string;
}

/** Mirrors public.call_participants, with optional joined user presentation. */
export interface CallParticipant {
  call_session_id: string;
  user_id: string;
  joined_at: string | null;
  left_at: string | null;
  participant_role: 'host' | 'participant';
  user?: { id: string; name: string; avatar?: string };
}

export type NotifyMode = 'all' | 'selected' | 'none';

export interface InviteeInfo {
  id: string;
  name: string;
  avatar?: string;
}

export interface StartCallParams {
  roomId: string;
  workspaceId?: string | null;
  projectId?: string | null;
  intent: CallIntent;
  userId: string;
  meetConfig?: GoogleMeetConfig;
  notifyMode?: NotifyMode;
  inviteeUserIds?: string[];
  invitees?: InviteeInfo[];
}

export interface CreatePlannedCallParams {
  /** ChatHub scope. Exactly one of roomId or calendarEventId is required. */
  roomId?: string | null;
  /** Calendar scope. The persisted event row is the authority for workspace/project. */
  calendarEventId?: string | null;
  workspaceId?: string | null;
  projectId?: string | null;
  intent: CallIntent;
  agenda: string;
  inviteeUserIds: string[];
  invitees?: InviteeInfo[];
  userId: string;
  /** @deprecated A room call event, not a direct notification insert, announces the call. */
  notifyInvitees?: boolean;
  notifyMode?: NotifyMode;
  meetConfig?: GoogleMeetConfig;
  scheduledStartAt?: string | null;
  /** Provider-adapter input; the canonical call row does not persist duration. */
  scheduledDurationMinutes?: number | null;
  /** Provider-adapter input; the canonical call row does not persist reminders. */
  reminderMinutes?: number | null;
}

export interface UpdatePlannedCallScheduleParams {
  scheduledStartAt?: string | null;
  scheduledDurationMinutes?: number | null;
  reminderMinutes?: number | null;
}

export interface CallServiceResult<T> {
  data: T | null;
  error: string | null;
  /** Internal lifecycle signal used to avoid rolling back a pre-existing active call. */
  created?: boolean;
}

export const PERMISSION_DENIED_ERROR = 'PERMISSION_DENIED';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CALL_TITLE_PREFIX: Record<CallIntent, string> = { call: 'Voice call', video: 'Video call' };

function isValidUuid(value: string): boolean {
  return UUID_REGEX.test(value);
}

function isValidId(value: string): boolean {
  return !!value?.trim() && (isDemoMode || isValidUuid(value));
}

function isPermissionError(error: { code?: string; message?: string }): boolean {
  if (error.code === '42501') return true;
  const message = (error.message || '').toLowerCase();
  return message.includes('permission denied')
    || message.includes('row-level security')
    || message.includes('policy');
}

function serviceError(prefix: string, error: { code?: string; message?: string }): string {
  console.error(`[callService] ${prefix}:`, error);
  return isPermissionError(error) ? PERMISSION_DENIED_ERROR : (error.message || `Failed to ${prefix}`);
}

function buildCallTitle(intent: CallIntent, agenda?: string | null): string {
  const cleanAgenda = agenda?.trim();
  return cleanAgenda ? `${CALL_TITLE_PREFIX[intent]} — ${cleanAgenda}` : CALL_TITLE_PREFIX[intent];
}

export function getCallIntent(session: Pick<CallSession, 'title'>): CallIntent {
  return session.title.toLocaleLowerCase().startsWith('voice call') ? 'call' : 'video';
}

export function getCallAgenda(session: Pick<CallSession, 'title'>): string | null {
  const separator = session.title.indexOf(' — ');
  return separator >= 0 ? session.title.slice(separator + 3).trim() || null : null;
}

export function getCallJoinUrl(session: Pick<CallSession, 'provider_room_id'>): string | null {
  const candidate = session.provider_room_id?.trim();
  if (!candidate) return null;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

interface CallScope {
  workspace_id: string;
  project_id: string | null;
  room_id: string | null;
  calendar_event_id: string | null;
}

/** Resolve scope from the RLS-filtered room rather than trusting caller scope. */
async function resolveRoomScope(
  roomId: string,
  workspaceHint?: string | null,
  projectHint?: string | null,
): Promise<CallServiceResult<CallScope>> {
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase
    .from('chat_rooms')
    .select('id, workspace_id, project_id')
    .eq('id', roomId)
    .single();

  if (error) return { data: null, error: serviceError('resolve room scope', error) };
  const scope = data as { id?: string; workspace_id?: string; project_id?: string | null } | null;
  if (!scope?.id || !scope.workspace_id) return { data: null, error: 'Conversation scope is incomplete' };
  if (workspaceHint && scope.workspace_id !== workspaceHint) {
    return { data: null, error: 'Call workspace does not match the active route scope' };
  }
  if (projectHint && scope.project_id !== projectHint) {
    return { data: null, error: 'Call project does not match the conversation scope' };
  }
  return {
    data: {
      room_id: scope.id,
      calendar_event_id: null,
      workspace_id: scope.workspace_id,
      project_id: scope.project_id ?? null,
    },
    error: null,
  };
}

/** Resolve scope from the RLS-filtered local calendar event. */
async function resolveCalendarEventScope(
  calendarEventId: string,
  workspaceHint?: string | null,
  projectHint?: string | null,
): Promise<CallServiceResult<CallScope>> {
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase
    .from('calendar_events')
    .select('id, workspace_id, project_id, status')
    .eq('id', calendarEventId)
    .single();

  if (error) return { data: null, error: serviceError('resolve calendar event scope', error) };
  const scope = data as { id?: string; workspace_id?: string; project_id?: string | null; status?: string } | null;
  if (!scope?.id || !scope.workspace_id) return { data: null, error: 'Calendar event scope is incomplete' };
  if (scope.status === 'cancelled') return { data: null, error: 'Cannot create a call for a cancelled calendar event' };
  if (workspaceHint && scope.workspace_id !== workspaceHint) {
    return { data: null, error: 'Call workspace does not match the calendar event scope' };
  }
  if (projectHint && scope.project_id !== projectHint) {
    return { data: null, error: 'Call project does not match the calendar event scope' };
  }
  return {
    data: {
      room_id: null,
      calendar_event_id: scope.id,
      workspace_id: scope.workspace_id,
      project_id: scope.project_id ?? null,
    },
    error: null,
  };
}

function nowIso(): string {
  return new Date().toISOString();
}

async function requireAuthenticatedUser(expectedUserId: string): Promise<CallServiceResult<string>> {
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) return { data: null, error: 'Your session has expired. Sign in and try again.' };
  if (data.user.id !== expectedUserId) return { data: null, error: PERMISSION_DENIED_ERROR };
  return { data: data.user.id, error: null };
}

// Demo uses the same canonical record shapes as connected mode.
const demoCallSessions = new Map<string, CallSession>();
const demoCallParticipants = new Map<string, CallParticipant[]>();

function demoUser(id: string, name: string, email: string): CallParticipant['user'] {
  return { id, name, avatar: demoAvatarFor(email, 64, name) };
}

(function seedDemoCalls() {
  const current = Date.now();
  const owner = 'cbfc74ff-8622-4227-a560-855e16a45e52';
  const endedAt = new Date(current - 60 * 60 * 1000).toISOString();
  const startedAt = new Date(current - 105 * 60 * 1000).toISOString();

  demoCallSessions.set('demo-call-session-1', {
    id: 'demo-call-session-1', workspace_id: 'demo-workspace', project_id: 'project-1', room_id: '1',
    calendar_event_id: null, title: buildCallTitle('video', 'Discuss payment gateway integration timeline and assign responsibilities'),
    provider: 'google_meet', provider_room_id: 'https://meet.google.com/abc-defg-hij', status: 'ended',
    starts_at: startedAt, ended_at: endedAt, created_by: owner,
    created_at: new Date(current - 2 * 60 * 60 * 1000).toISOString(),
  });
  demoCallSessions.set('demo-call-session-2', {
    id: 'demo-call-session-2', workspace_id: 'demo-workspace', project_id: 'project-1', room_id: '1',
    calendar_event_id: null, title: buildCallTitle('video', 'Quick sync on API integration blockers and next steps'),
    provider: 'google_meet', provider_room_id: 'https://meet.google.com/xyz-uvwx-yz', status: 'ended',
    starts_at: new Date(current - 3 * 60 * 60 * 1000).toISOString(),
    ended_at: new Date(current - 165 * 60 * 1000).toISOString(), created_by: owner,
    created_at: new Date(current - 3 * 60 * 60 * 1000).toISOString(),
  });
  demoCallSessions.set('demo-call-session-3', {
    id: 'demo-call-session-3', workspace_id: 'demo-workspace', project_id: 'project-1', room_id: '1',
    calendar_event_id: null, title: buildCallTitle('video', 'Review final designs and approve for development handoff'),
    provider: 'google_meet', provider_room_id: 'https://meet.google.com/new-call-123', status: 'planned',
    starts_at: new Date(current + 2 * 60 * 60 * 1000).toISOString(), ended_at: null, created_by: owner,
    created_at: new Date(current - 30 * 60 * 1000).toISOString(),
  });

  demoCallParticipants.set('demo-call-session-3', [
    { call_session_id: 'demo-call-session-3', user_id: owner, joined_at: null, left_at: null, participant_role: 'host', user: demoUser(owner, 'Sarah Johnson', 'sarah.johnson@northstarholdings.eu') },
    { call_session_id: 'demo-call-session-3', user_id: '0', joined_at: null, left_at: null, participant_role: 'participant', user: demoUser('0', 'Tania Admin', 'owner@ax1.capital') },
    { call_session_id: 'demo-call-session-3', user_id: '2', joined_at: null, left_at: null, participant_role: 'participant', user: demoUser('2', 'Sam System', 'system.admin@ax1.capital') },
  ]);
  demoCallParticipants.set('demo-call-session-2', [
    { call_session_id: 'demo-call-session-2', user_id: owner, joined_at: startedAt, left_at: endedAt, participant_role: 'host', user: demoUser(owner, 'Sarah Johnson', 'sarah.johnson@northstarholdings.eu') },
    { call_session_id: 'demo-call-session-2', user_id: '0', joined_at: startedAt, left_at: endedAt, participant_role: 'participant', user: demoUser('0', 'Tania Admin', 'owner@ax1.capital') },
  ]);
  demoCallParticipants.set('demo-call-session-1', [
    { call_session_id: 'demo-call-session-1', user_id: owner, joined_at: startedAt, left_at: endedAt, participant_role: 'host', user: demoUser(owner, 'Sarah Johnson', 'sarah.johnson@northstarholdings.eu') },
    { call_session_id: 'demo-call-session-1', user_id: '0', joined_at: startedAt, left_at: endedAt, participant_role: 'participant', user: demoUser('0', 'Tania Admin', 'owner@ax1.capital') },
  ]);
})();

function getDemoActiveCall(roomId: string): CallSession | null {
  return [...demoCallSessions.values()].find((session) => session.room_id === roomId && session.status === 'active') ?? null;
}

function getDemoCallSession(callSessionId: string): CallSession | null {
  const existing = demoCallSessions.get(callSessionId);
  if (existing) return existing;
  const roomMatch = callSessionId.match(/^demo-call-(\d+)$/);
  if (!roomMatch || roomMatch[1] === '1') return null;
  const start = new Date(Date.now() - 20 * 60 * 1000).toISOString();
  const generated: CallSession = {
    id: callSessionId, workspace_id: 'demo-workspace', project_id: null, room_id: roomMatch[1],
    calendar_event_id: null, title: buildCallTitle('video'), provider: 'google_meet',
    provider_room_id: `https://meet.google.com/demo-${roomMatch[1]}`, status: 'ended', starts_at: start,
    ended_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(), created_by: '2', created_at: start,
  };
  demoCallSessions.set(callSessionId, generated);
  return generated;
}

type CallParticipantRpc = 'ax1_join_call' | 'ax1_mark_call_attendance' | 'ax1_leave_call';

function mapCallParticipant(row: Record<string, unknown>): CallParticipant {
  const related = Array.isArray(row.user) ? row.user[0] : row.user;
  const user = related && typeof related === 'object' ? related as Record<string, unknown> : null;
  return {
    call_session_id: String(row.call_session_id),
    user_id: String(row.user_id),
    joined_at: typeof row.joined_at === 'string' ? row.joined_at : null,
    left_at: typeof row.left_at === 'string' ? row.left_at : null,
    participant_role: row.participant_role === 'host' ? 'host' : 'participant',
    user: user ? {
      id: String(user.id),
      name: String(user.full_name || user.name || 'Unknown user'),
      avatar: typeof user.avatar_url === 'string' ? user.avatar_url : undefined,
    } : undefined,
  };
}

async function mutateOwnParticipant(
  callSessionId: string,
  operation: CallParticipantRpc,
): Promise<CallServiceResult<CallParticipant>> {
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase.rpc(operation, { p_call_id: callSessionId });
  if (error) return { data: null, error: serviceError('update call participation', error) };
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
  if (!row) return { data: null, error: 'Call participation was not returned' };
  return { data: mapCallParticipant(row), error: null };
}

async function inviteCallParticipants(
  callSessionId: string,
  inviteeUserIds: string[],
): Promise<CallServiceResult<CallParticipant[]>> {
  if (!inviteeUserIds.length) return { data: [], error: null };
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase.rpc('ax1_invite_call_participants', {
    p_call_id: callSessionId,
    p_user_ids: [...new Set(inviteeUserIds)],
  });
  if (error) return { data: null, error: serviceError('invite call participants', error) };
  return {
    data: ((data || []) as Record<string, unknown>[]).map((row) => mapCallParticipant(row)),
    error: null,
  };
}

async function rollbackCreatedCall(
  session: CallSession,
  actorId: string,
  cause: string,
): Promise<CallServiceResult<CallSession>> {
  if (!supabase) return { data: session, error: cause };
  const { error } = await supabase.from('call_sessions')
    .update({ status: 'cancelled', ended_at: nowIso() })
    .eq('id', session.id)
    .eq('created_by', actorId)
    .select()
    .single();
  if (error) {
    return { data: session, error: `${cause}. The incomplete call could not be cancelled: ${serviceError('cancel incomplete call', error)}` };
  }
  return { data: null, error: cause };
}

export async function getActiveCall(roomId: string): Promise<CallServiceResult<CallSession>> {
  if (!isValidId(roomId)) return { data: null, error: 'Invalid room ID' };
  if (isDemoMode) return { data: getDemoActiveCall(roomId), error: null };
  if (!supabase) return { data: null, error: 'Call service is unavailable' };

  const { data, error } = await supabase.from('call_sessions').select('*')
    .eq('room_id', roomId).eq('status', 'active')
    .order('starts_at', { ascending: false }).limit(1).maybeSingle();
  if (error) return { data: null, error: serviceError('load active call', error) };
  return { data: data as CallSession | null, error: null };
}

export async function startCall(params: StartCallParams): Promise<CallServiceResult<CallSession>> {
  const { roomId, workspaceId, projectId, intent, userId, meetConfig = {} } = params;
  if (!isValidId(roomId)) return { data: null, error: 'Invalid room ID' };
  if (!isValidId(userId)) return { data: null, error: 'Invalid user ID' };
  if (projectId !== null && projectId !== undefined && !projectId.trim()) return { data: null, error: 'Invalid project ID' };
  if (intent !== 'call' && intent !== 'video') return { data: null, error: 'Invalid call intent' };

  const existing = await getActiveCall(roomId);
  if (existing.error) return existing;
  if (existing.data) return { ...existing, created: false };

  const joinUrl = resolveGoogleMeetJoinUrl(meetConfig);
  const timestamp = nowIso();
  if (isDemoMode) {
    const session: CallSession = {
      id: `demo-call-${Date.now()}`, workspace_id: workspaceId || 'demo-workspace', project_id: projectId ?? null,
      room_id: roomId, calendar_event_id: null, title: buildCallTitle(intent), provider: PROVIDER_NAME,
      provider_room_id: joinUrl, status: 'active', starts_at: timestamp, ended_at: null,
      created_by: userId, created_at: timestamp,
    };
    demoCallSessions.set(session.id, session);
    return { data: session, error: null, created: true };
  }

  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const actor = await requireAuthenticatedUser(userId);
  if (actor.error || !actor.data) return { data: null, error: actor.error || PERMISSION_DENIED_ERROR };
  const scope = await resolveRoomScope(roomId, workspaceId, projectId);
  if (scope.error || !scope.data) return { data: null, error: scope.error || 'Conversation scope is unavailable' };
  const { data, error } = await supabase.from('call_sessions').insert({
    workspace_id: scope.data.workspace_id,
    project_id: scope.data.project_id,
    room_id: scope.data.room_id,
    title: buildCallTitle(intent),
    provider: PROVIDER_NAME,
    provider_room_id: joinUrl,
    status: 'active',
    starts_at: timestamp,
    created_by: actor.data,
  }).select().single();

  if (error) {
    if (error.code === '23505') {
      const concurrent = await getActiveCall(roomId);
      if (concurrent.data) return { ...concurrent, created: false };
    }
    return { data: null, error: serviceError('start call', error) };
  }
  return { data: data as CallSession, error: null, created: true };
}

export async function endCall(callSessionId: string): Promise<CallServiceResult<CallSession>> {
  if (!isValidId(callSessionId)) return { data: null, error: 'Invalid call session ID' };
  if (isDemoMode) {
    const session = demoCallSessions.get(callSessionId);
    if (!session) return { data: null, error: 'Call session not found' };
    if (session.status === 'planned' || session.status === 'cancelled') {
      const cancelled = { ...session, status: 'cancelled' as const, ended_at: session.ended_at ?? nowIso() };
      demoCallSessions.delete(callSessionId);
      demoCallParticipants.delete(callSessionId);
      return { data: cancelled, error: null };
    }
    if (session.status === 'active') {
      session.status = 'ended';
      session.ended_at = nowIso();
    }
    return { data: { ...session }, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };

  const current = await getCallSession(callSessionId);
  if (current.error || !current.data) return current;
  if (current.data.status === 'ended') return current;

  // A not-yet-started call has no attendance history to preserve. Remove it
  // through the governed DELETE policy so deleting its calendar event cannot
  // leave a roomless/eventless row after the FK is cleared. Active calls retain
  // the existing ended transition and remain available as history.
  if (current.data.status === 'planned' || current.data.status === 'cancelled') {
    const { data, error } = await supabase.from('call_sessions')
      .delete()
      .eq('id', callSessionId)
      .eq('status', current.data.status)
      .select()
      .single();
    if (error) return { data: null, error: serviceError('cancel planned call', error) };
    return {
      data: { ...(data as CallSession), status: 'cancelled', ended_at: current.data.ended_at ?? nowIso() },
      error: null,
    };
  }

  const { data, error } = await supabase.from('call_sessions')
    .update({ status: 'ended', ended_at: nowIso() })
    .eq('id', callSessionId)
    .eq('status', 'active')
    .select()
    .single();
  if (error) return { data: null, error: serviceError('end call', error) };
  return { data: data as CallSession, error: null };
}

export function joinCall(callSession: CallSession): void {
  const joinUrl = getCallJoinUrl(callSession);
  if (!joinUrl) throw new Error('This call does not have a valid HTTPS meeting link');
  window.open(joinUrl, '_blank', 'noopener,noreferrer');
}

export function getCallDuration(session: CallSession): number | null {
  if (!session.starts_at || !session.ended_at) return null;
  return Math.max(0, Math.floor((new Date(session.ended_at).getTime() - new Date(session.starts_at).getTime()) / 1000));
}

export function formatCallDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes < 60) return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export async function startInstantCall(params: StartCallParams): Promise<CallServiceResult<CallSession>> {
  const result = await startCall(params);
  if (result.error || !result.data) return result;
  const session = result.data;
  const inviteeIds = params.notifyMode === 'none'
    ? []
    : (params.inviteeUserIds ?? params.invitees?.map((invitee) => invitee.id) ?? [])
      .filter((id) => id !== params.userId);

  if (isDemoMode) {
    const timestamp = nowIso();
    const invitees: InviteeInfo[] = (params.invitees ?? inviteeIds.map((id) => ({ id, name: `User ${id}` })))
      .filter((invitee) => inviteeIds.includes(invitee.id));
    demoCallParticipants.set(session.id, [
      { call_session_id: session.id, user_id: params.userId, joined_at: timestamp, left_at: null, participant_role: 'host', user: { id: params.userId, name: `User ${params.userId}` } },
      ...invitees.filter((invitee) => invitee.id !== params.userId).map((invitee) => ({
        call_session_id: session.id, user_id: invitee.id, joined_at: null, left_at: null, participant_role: 'participant' as const,
        user: { id: invitee.id, name: invitee.name, avatar: invitee.avatar },
      })),
    ]);
  } else {
    const participant = await mutateOwnParticipant(session.id, 'ax1_mark_call_attendance');
    if (participant.error) {
      return result.created
        ? rollbackCreatedCall(session, params.userId, participant.error)
        : { data: session, error: participant.error };
    }
    const invitations = await inviteCallParticipants(session.id, inviteeIds);
    if (invitations.error) {
      return result.created
        ? rollbackCreatedCall(session, params.userId, invitations.error)
        : { data: session, error: invitations.error };
    }
  }
  return { data: session, error: null, created: result.created };
}

export async function createPlannedCall(params: CreatePlannedCallParams): Promise<CallServiceResult<CallSession>> {
  const { roomId, calendarEventId, workspaceId, projectId, intent, agenda, inviteeUserIds, invitees = [], userId, meetConfig = {}, scheduledStartAt, notifyMode = 'all' } = params;
  const hasRoomScope = !!roomId;
  const hasCalendarScope = !!calendarEventId;
  if (hasRoomScope === hasCalendarScope) return { data: null, error: 'Choose exactly one call scope: conversation or calendar event' };
  if (roomId && !isValidId(roomId)) return { data: null, error: 'Invalid room ID' };
  if (calendarEventId && !isValidId(calendarEventId)) return { data: null, error: 'Invalid calendar event ID' };
  if (!isValidId(userId)) return { data: null, error: 'Invalid user ID' };
  if (!agenda?.trim()) return { data: null, error: 'Agenda is required for planned calls' };
  if (notifyMode === 'selected' && !inviteeUserIds?.length) return { data: null, error: 'Select at least one room participant' };
  if (scheduledStartAt && Number.isNaN(new Date(scheduledStartAt).getTime())) return { data: null, error: 'Invalid scheduled start time' };
  const invitedUserIds = notifyMode === 'none' ? [] : inviteeUserIds.filter((id) => id !== userId);

  const joinUrl = resolveGoogleMeetJoinUrl(meetConfig);
  const timestamp = nowIso();
  if (isDemoMode) {
    const session: CallSession = {
      id: `demo-call-${Date.now()}`, workspace_id: workspaceId || 'demo-workspace', project_id: projectId ?? null,
      room_id: roomId ?? null, calendar_event_id: calendarEventId ?? null, title: buildCallTitle(intent, agenda), provider: PROVIDER_NAME,
      provider_room_id: joinUrl, status: 'planned', starts_at: scheduledStartAt ?? null, ended_at: null,
      created_by: userId, created_at: timestamp,
    };
    demoCallSessions.set(session.id, session);
    demoCallParticipants.set(session.id, [
      { call_session_id: session.id, user_id: userId, joined_at: null, left_at: null, participant_role: 'host', user: { id: userId, name: `User ${userId}` } },
      ...invitedUserIds.map((id) => {
        const invitee = invitees.find((candidate) => candidate.id === id);
        return { call_session_id: session.id, user_id: id, joined_at: null, left_at: null, participant_role: 'participant' as const, user: invitee ? { id, name: invitee.name, avatar: invitee.avatar } : { id, name: `User ${id}` } };
      }),
    ]);
    return { data: session, error: null };
  }

  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const actor = await requireAuthenticatedUser(userId);
  if (actor.error || !actor.data) return { data: null, error: actor.error || PERMISSION_DENIED_ERROR };
  const scope = roomId
    ? await resolveRoomScope(roomId, workspaceId, projectId)
    : await resolveCalendarEventScope(calendarEventId!, workspaceId, projectId);
  if (scope.error || !scope.data) return { data: null, error: scope.error || 'Call scope is unavailable' };
  const { data, error } = await supabase.from('call_sessions').insert({
    workspace_id: scope.data.workspace_id,
    project_id: scope.data.project_id,
    room_id: scope.data.room_id,
    calendar_event_id: scope.data.calendar_event_id,
    title: buildCallTitle(intent, agenda),
    provider: PROVIDER_NAME,
    provider_room_id: joinUrl,
    status: 'planned',
    starts_at: scheduledStartAt ?? null,
    created_by: actor.data,
  }).select().single();
  if (error) return { data: null, error: serviceError('create planned call', error) };

  const session = data as CallSession;
  const participant = await mutateOwnParticipant(session.id, 'ax1_join_call');
  if (participant.error) return rollbackCreatedCall(session, actor.data, participant.error);
  const invitations = await inviteCallParticipants(session.id, invitedUserIds);
  if (invitations.error) return rollbackCreatedCall(session, actor.data, invitations.error);
  return { data: session, error: null, created: true };
}

export async function updatePlannedCallSchedule(callSessionId: string, params: UpdatePlannedCallScheduleParams): Promise<CallServiceResult<CallSession>> {
  if (!isValidId(callSessionId)) return { data: null, error: 'Invalid call session ID' };
  if (params.scheduledStartAt !== undefined && params.scheduledStartAt !== null && Number.isNaN(new Date(params.scheduledStartAt).getTime())) {
    return { data: null, error: 'Invalid scheduled start time' };
  }
  if (isDemoMode) {
    const session = demoCallSessions.get(callSessionId);
    if (!session || session.status !== 'planned') return { data: null, error: 'Planned call not found' };
    if (params.scheduledStartAt !== undefined) session.starts_at = params.scheduledStartAt;
    return { data: { ...session }, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  if (params.scheduledStartAt === undefined) return getCallSession(callSessionId);
  const { data, error } = await supabase.from('call_sessions').update({ starts_at: params.scheduledStartAt })
    .eq('id', callSessionId).eq('status', 'planned').select().single();
  if (error) return { data: null, error: serviceError('update planned call schedule', error) };
  return { data: data as CallSession, error: null };
}

/** Browser reminder state is local because the canonical schema has no reminder column. */
export async function markReminderSent(callSessionId: string): Promise<CallServiceResult<CallSession>> {
  return getCallSession(callSessionId);
}

export interface UpdateCallProviderInfoParams {
  provider: CallProvider;
  providerRoomId?: string | null;
}

export async function updateCallProviderInfo(callSessionId: string, params: UpdateCallProviderInfoParams): Promise<CallServiceResult<CallSession>> {
  if (!isValidId(callSessionId)) return { data: null, error: 'Invalid call session ID' };
  if (isDemoMode) {
    const session = demoCallSessions.get(callSessionId);
    if (!session) return { data: null, error: 'Call session not found' };
    session.provider = params.provider;
    if (params.providerRoomId !== undefined) session.provider_room_id = params.providerRoomId;
    return { data: { ...session }, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const payload: { provider: CallProvider; provider_room_id?: string | null } = { provider: params.provider };
  if (params.providerRoomId !== undefined) payload.provider_room_id = params.providerRoomId;
  const { data, error } = await supabase.from('call_sessions').update(payload).eq('id', callSessionId).select().single();
  if (error) return { data: null, error: serviceError('update provider info', error) };
  return { data: data as CallSession, error: null };
}

export async function updateCallMeetUrl(callSessionId: string, meetUrl: string): Promise<CallServiceResult<CallSession>> {
  let parsed: URL;
  try {
    parsed = new URL(meetUrl);
  } catch {
    return { data: null, error: 'Invalid meeting URL' };
  }
  if (parsed.protocol !== 'https:') return { data: null, error: 'Meeting URL must use HTTPS' };
  if (!isValidId(callSessionId)) return { data: null, error: 'Invalid call session ID' };
  if (isDemoMode) {
    const session = demoCallSessions.get(callSessionId);
    if (!session) return { data: null, error: 'Call session not found' };
    session.provider_room_id = parsed.toString();
    return { data: { ...session }, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase.from('call_sessions').update({ provider_room_id: parsed.toString() })
    .eq('id', callSessionId).select().single();
  if (error) return { data: null, error: serviceError('update meeting URL', error) };
  return { data: data as CallSession, error: null };
}

export async function startPlannedCall(callSessionId: string): Promise<CallServiceResult<CallSession>> {
  if (!isValidId(callSessionId)) return { data: null, error: 'Invalid call session ID' };
  const timestamp = nowIso();
  if (isDemoMode) {
    const session = demoCallSessions.get(callSessionId);
    if (!session || session.status !== 'planned') return { data: null, error: 'Planned call not found' };
    session.status = 'active';
    session.starts_at = timestamp;
    return { data: { ...session }, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase.from('call_sessions').update({ status: 'active', starts_at: timestamp })
    .eq('id', callSessionId).eq('status', 'planned').select().single();
  if (error) return { data: null, error: serviceError('start planned call', error) };
  const attendance = await mutateOwnParticipant(callSessionId, 'ax1_mark_call_attendance');
  if (attendance.error) return { data: data as CallSession, error: attendance.error };
  return { data: data as CallSession, error: null };
}

export async function getCallSession(callSessionId: string): Promise<CallServiceResult<CallSession>> {
  if (!isValidId(callSessionId)) return { data: null, error: 'Invalid call session ID' };
  if (isDemoMode) return { data: getDemoCallSession(callSessionId), error: null };
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase.from('call_sessions').select('*').eq('id', callSessionId).single();
  if (error) return { data: null, error: serviceError('load call session', error) };
  return { data: data as CallSession, error: null };
}

export async function getRoomCallSessions(roomId: string, statuses?: CallStatus[]): Promise<CallServiceResult<CallSession[]>> {
  if (!isValidId(roomId)) return { data: null, error: 'Invalid room ID' };
  if (isDemoMode) {
    const sessions = [...demoCallSessions.values()]
      .filter((session) => session.room_id === roomId && (!statuses?.length || statuses.includes(session.status)))
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    return { data: sessions, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  let query = supabase.from('call_sessions').select('*').eq('room_id', roomId).order('created_at', { ascending: false });
  if (statuses?.length) query = query.in('status', statuses);
  const { data, error } = await query;
  if (error) return { data: null, error: serviceError('load room calls', error) };
  return { data: (data || []) as CallSession[], error: null };
}

export async function getPlannedCalls(roomId?: string): Promise<CallServiceResult<CallSession[]>> {
  if (isDemoMode) {
    return {
      data: [...demoCallSessions.values()].filter((session) => session.status === 'planned' && !!session.starts_at && (!roomId || session.room_id === roomId)),
      error: null,
    };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  let query = supabase.from('call_sessions').select('*').eq('status', 'planned').not('starts_at', 'is', null);
  if (roomId) query = query.eq('room_id', roomId);
  const { data, error } = await query;
  if (error) return { data: null, error: serviceError('load planned calls', error) };
  return { data: (data || []) as CallSession[], error: null };
}

export async function getCallParticipants(callSessionId: string): Promise<CallServiceResult<CallParticipant[]>> {
  if (!isValidId(callSessionId)) return { data: null, error: 'Invalid call session ID' };
  if (isDemoMode) return { data: demoCallParticipants.get(callSessionId) || [], error: null };
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase.from('call_participants')
    .select('call_session_id, user_id, joined_at, left_at, participant_role, user:user_id(id, name, full_name, avatar_url)')
    .eq('call_session_id', callSessionId);
  if (error) return { data: null, error: serviceError('load call participants', error) };
  const participants = (data || []).map((row: Record<string, unknown>) => mapCallParticipant(row));
  return { data: participants, error: null };
}

export async function confirmAttendance(callSessionId: string, userId: string): Promise<CallServiceResult<CallParticipant>> {
  if (!isValidId(callSessionId) || !isValidId(userId)) return { data: null, error: 'Invalid IDs' };
  const timestamp = nowIso();
  if (isDemoMode) {
    const participants = demoCallParticipants.get(callSessionId) || [];
    let participant = participants.find((candidate) => candidate.user_id === userId);
    if (!participant) {
      const session = demoCallSessions.get(callSessionId);
      participant = {
        call_session_id: callSessionId,
        user_id: userId,
        joined_at: timestamp,
        left_at: null,
        participant_role: session?.created_by === userId ? 'host' : 'participant',
      };
      participants.push(participant);
      demoCallParticipants.set(callSessionId, participants);
    } else {
      participant.joined_at = timestamp;
      participant.left_at = null;
    }
    return { data: { ...participant }, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const actor = await requireAuthenticatedUser(userId);
  if (actor.error || !actor.data) return { data: null, error: actor.error || PERMISSION_DENIED_ERROR };
  return mutateOwnParticipant(callSessionId, 'ax1_mark_call_attendance');
}

export async function leaveCallSession(callSessionId: string, userId: string): Promise<CallServiceResult<CallParticipant>> {
  if (!isValidId(callSessionId) || !isValidId(userId)) return { data: null, error: 'Invalid IDs' };
  const timestamp = nowIso();
  if (isDemoMode) {
    const participant = (demoCallParticipants.get(callSessionId) || []).find((candidate) => candidate.user_id === userId);
    if (!participant) return { data: null, error: 'Call participant not found' };
    participant.left_at = timestamp;
    return { data: { ...participant }, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const actor = await requireAuthenticatedUser(userId);
  if (actor.error || !actor.data) return { data: null, error: actor.error || PERMISSION_DENIED_ERROR };
  return mutateOwnParticipant(callSessionId, 'ax1_leave_call');
}

export async function updateCallAgenda(callSessionId: string, agenda: string): Promise<CallServiceResult<CallSession>> {
  if (!agenda.trim()) return { data: null, error: 'Call title is required' };
  const current = await getCallSession(callSessionId);
  if (current.error || !current.data) return current;
  const title = buildCallTitle(getCallIntent(current.data), agenda);
  if (isDemoMode) {
    current.data.title = title;
    return { data: { ...current.data }, error: null };
  }
  if (!supabase) return { data: null, error: 'Call service is unavailable' };
  const { data, error } = await supabase.from('call_sessions').update({ title }).eq('id', callSessionId).select().single();
  if (error) return { data: null, error: serviceError('update call title', error) };
  return { data: data as CallSession, error: null };
}
